import { Box, FormControl } from '@mui/material';
import { type ReactNode, useId, useState } from 'react';

import { FormHelperText } from '../FormHelperText/FormHelperText';
import { FormLabel } from '../FormLabel/FormLabel';
import { Grid } from '../Grid/Grid';
import { Checkbox } from './Checkbox';

interface Props<T extends string> {
  label: string;
  options: { value: T; label: string | ReactNode; disabled?: boolean }[];
  checked: T[];
  onChange: (checked: T[]) => void;
  required?: boolean;
  errorText?: string;
  helperText?: string;
  overrideVisited?: boolean;
  optionsMinWidth?: string;
}

export function CheckboxGroup<T extends string>({
  label,
  options,
  checked,
  onChange,
  required = false,
  errorText = '',
  helperText = '',
  overrideVisited = false,
  optionsMinWidth = '4rem',
}: Props<T>) {
  const generatedId = useId();
  const labelId = `${generatedId}-label`;
  const helperTextId = `${generatedId}-helper-text`;

  const [visited, setVisited] = useState(false);

  function handleChange(optionValue: T): void {
    setVisited(true);

    onChange(
      checked.includes(optionValue)
        ? options
            .map((option) => option.value)
            .filter((value) => checked.includes(value) && value !== optionValue)
        : options
            .map((option) => option.value)
            .filter((value) => checked.includes(value) || value === optionValue)
    );
  }

  const showError = !!errorText && (visited || overrideVisited);

  return (
    <FormControl fullWidth>
      <FormLabel
        componentId={generatedId}
        id={labelId}
        label={label}
        required={required}
        valid={!errorText}
      />

      {options.length <= 8 &&
        options.map((option) => (
          <Checkbox
            key={option.value}
            label={option.label}
            checked={checked.includes(option.value)}
            disabled={option.disabled}
            onChange={() => handleChange(option.value)}
          />
        ))}

      {options.length > 8 && (
        <Grid minColumnWidth={optionsMinWidth}>
          {options.map((option) => (
            <Box key={option.value} mr="0.7rem">
              <Checkbox
                label={option.label}
                checked={checked.includes(option.value)}
                disabled={option.disabled}
                onChange={() => handleChange(option.value)}
              />
            </Box>
          ))}
        </Grid>
      )}

      <FormHelperText
        id={helperTextId}
        showError={showError}
        text={showError ? errorText : helperText}
      />
    </FormControl>
  );
}
